import { useState, useEffect } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Input } from "@/components/ui/input";
import { ArrowLeft, Save, Settings, AlertCircle } from "lucide-react";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";

import { API, BACKEND_URL, getAuthHeaders } from "@/lib/api";

// Feature-Definitionen mit benutzerfreundlichen Labels
const FEATURE_LABELS = {
  // Boolean Features
  music_upload: { label: "Musik Upload", category: "Media", type: "boolean" },
  video_upload: { label: "Video Upload", category: "Media", type: "boolean" },
  premium_templates: { label: "Premium Templates", category: "Design", type: "boolean" },
  remove_branding: { label: "Branding Entfernen", category: "Branding", type: "boolean" },
  password_protection: { label: "Passwortschutz", category: "Security", type: "boolean" },
  analytics: { label: "Analytics", category: "Analytics", type: "boolean" },
  team_collaboration: { label: "Team-Funktion", category: "Collaboration", type: "boolean" },
  priority_support: { label: "Priority Support", category: "Support", type: "boolean" },

  // Numeric Features
  max_events: { label: "Max. Events", category: "Limits", type: "number", suffix: "Events (-1 = unbegrenzt)" },
  max_guests: { label: "Max. Gäste", category: "Limits", type: "number", suffix: "Gäste (-1 = unbegrenzt)" },
  max_photos: { label: "Max. Fotos", category: "Limits", type: "number", suffix: "Fotos (-1 = unbegrenzt)" },
  max_videos: { label: "Max. Videos", category: "Limits", type: "number", suffix: "Videos" },
  storage_limit_mb: { label: "Speicherplatz", category: "Limits", type: "number", suffix: "MB" },
  event_duration_days: { label: "Event-Dauer", category: "Limits", type: "number", suffix: "Tage (-1 = unbegrenzt)" }
};

export default function AdminFeatureSettings() {
  const navigate = useNavigate();
  const [packages, setPackages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchPackages();
  }, []);

  const fetchPackages = async () => {
    try {
      setError(null);
      const response = await axios.get(`${API}/admin/features`, {
        headers: getAuthHeaders(),
        withCredentials: true,
      });

      // Ensure all packages have a features object
      const packagesWithFeatures = response.data.map(pkg => ({
        ...pkg,
        features: pkg.features || {}
      }));

      setPackages(packagesWithFeatures);
    } catch (error) {
      console.error("Failed to fetch packages:", error);
      setError(error.response?.data?.detail || "Fehler beim Laden der Pakete");
      toast.error("Fehler beim Laden der Pakete");
    } finally {
      setLoading(false);
    }
  };

  const handleFeatureToggle = (packageId, featureKey, value) => {
    setPackages(packages.map(pkg => {
      if (pkg.id === packageId) {
        return {
          ...pkg,
          features: {
            ...pkg.features,
            [featureKey]: value
          }
        };
      }
      return pkg;
    }));
  };

  const handleSaveAll = async () => {
    setSaving(true);
    try {
      let successCount = 0;
      for (const pkg of packages) {
        try {
          await axios.patch(
            `${API}/admin/packages/${pkg.id}`,
            { features: pkg.features },
            {
              headers: getAuthHeaders(),
              withCredentials: true
            }
          );
          successCount++;
        } catch (err) {
          console.error(`Failed to save package ${pkg.name}:`, err);
          toast.error(`Fehler beim Speichern von ${pkg.name}`);
        }
      }
      if (successCount === packages.length) {
        toast.success("Alle Änderungen gespeichert!");
      } else {
        toast.warning(`${successCount}/${packages.length} Pakete gespeichert`);
      }
    } catch (error) {
      console.error("Failed to save packages:", error);
      toast.error("Fehler beim Speichern");
    } finally {
      setSaving(false);
    }
  };

  // Group features by category
  const groupedFeatures = Object.entries(FEATURE_LABELS).reduce((acc, [key, data]) => {
    if (!acc[data.category]) {
      acc[data.category] = [];
    }
    acc[data.category].push({ key, ...data });
    return acc;
  }, {});

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background pb-40">
      {/* Header */}
      <header className="sticky top-0 z-40 bg-background/80 backdrop-blur-xl border-b border-border/40">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button
                variant="ghost"
                size="icon"
                onClick={() => navigate("/admin")}
                className="rounded-full"
              >
                <ArrowLeft className="w-5 h-5" />
              </Button>
              <div>
                <h1 className="text-2xl font-bold flex items-center gap-2">
                  <Settings className="w-6 h-6" />
                  Feature-Verwaltung
                </h1>
                <p className="text-sm text-muted-foreground">
                  Konfiguriere Features für jede Preisklasse
                </p>
              </div>
            </div>
            <Button
              onClick={handleSaveAll}
              disabled={saving}
              className="rounded-full"
            >
              <Save className="w-4 h-4 mr-2" />
              {saving ? "Speichern..." : "Alle Speichern"}
            </Button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 py-8">
        {/* Error State */}
        {error && (
          <Card className="p-6 mb-6 bg-destructive/10 border-destructive/20">
            <div className="flex items-center gap-2 text-destructive">
              <AlertCircle className="w-5 h-5" />
              <p className="font-medium">{error}</p>
            </div>
          </Card>
        )}

        {/* Package Overview Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
          {packages.map((pkg) => (
            <Card key={pkg.id} className="p-6">
              <div className="text-center">
                <h3 className="text-lg font-bold mb-1">{pkg.name}</h3>
                <p className="text-2xl font-bold text-primary mb-2">
                  {pkg.price > 0 ? `€${pkg.price}` : "Kostenlos"}
                </p>
                <p className="text-xs text-muted-foreground">
                  {Object.values(pkg.features || {}).filter(v => v === true).length} Boolean Features
                </p>
              </div>
            </Card>
          ))}
        </div>

        {/* Feature Configuration */}
        <div className="space-y-6">
          {Object.entries(groupedFeatures).map(([category, features]) => (
            <Card key={category} className="p-6">
              <h2 className="text-xl font-bold mb-4">{category}</h2>

              <div className="space-y-4">
                {features.map((feature) => (
                  <div key={feature.key} className="border-b border-border/50 pb-4 last:border-0 last:pb-0">
                    <div className="mb-3">
                      <h3 className="font-medium text-foreground">{feature.label}</h3>
                      {feature.suffix && (
                        <p className="text-xs text-muted-foreground">{feature.suffix}</p>
                      )}
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      {packages.map((pkg) => (
                        <div key={pkg.id} className="flex items-center justify-between p-3 rounded-lg bg-secondary/20">
                          <Label htmlFor={`${pkg.id}-${feature.key}`} className="text-sm font-medium flex-shrink-0">
                            {pkg.name}
                          </Label>

                          {feature.type === "boolean" ? (
                            <Switch
                              id={`${pkg.id}-${feature.key}`}
                              checked={pkg.features?.[feature.key] || false}
                              onCheckedChange={(checked) => handleFeatureToggle(pkg.id, feature.key, checked)}
                            />
                          ) : (
                            <Input
                              id={`${pkg.id}-${feature.key}`}
                              type="number"
                              value={pkg.features?.[feature.key] ?? 0}
                              onChange={(e) => handleFeatureToggle(pkg.id, feature.key, parseInt(e.target.value) || 0)}
                              className="w-24 h-8 text-right"
                            />
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </Card>
          ))}
        </div>

        {/* Info Card */}
        <Card className="p-6 mt-8 bg-primary/5 border-primary/20">
          <h3 className="font-semibold mb-2 flex items-center gap-2">
            <Settings className="w-5 h-5 text-primary" />
            Hinweis zur Feature-Verwaltung
          </h3>
          <ul className="text-sm text-muted-foreground space-y-1 list-disc list-inside">
            <li>Änderungen werden für alle neuen Benutzer sofort wirksam</li>
            <li>Bestehende Benutzer behalten ihre aktuellen Features bis zur nächsten Paket-Erneuerung</li>
            <li>Deaktivierte Features werden in der UI mit einem Lock-Symbol angezeigt</li>
            <li>PRO-User haben immer vollen Zugriff auf alle Features (Admin-Override)</li>
          </ul>
        </Card>
      </div>
    </div>
  );
}
